import { apiUrl } from "../utils/api";

export type MailboxMailAccess = "NONE" | "READ" | "SEND" | "FULL";
export type MailboxCalendarAccess = "NONE" | "READ" | "WRITE";
export type MailboxType = "user" | "room" | "shared";

/**
 * Admin view of one User<->Mailbox assignment (admin/mailbox-access).
 * Mirrors the UserMailboxAccess entity returned by the backend.
 */
export interface MailboxAccessAdmin {
    id: string;
    tenantId: string;
    userId: string;
    mailboxAddress: string;
    mailAccess: MailboxMailAccess;
    calendarAccess: MailboxCalendarAccess;
    canCreateTeamsMeetings: boolean;
    mailboxType: MailboxType;
    displayName: string | null;
    createdAt: string;
    updatedAt: string;
}

export interface CreateMailboxAccessInput {
    userId: string;
    mailboxAddress: string;
    mailAccess?: MailboxMailAccess;
    calendarAccess?: MailboxCalendarAccess;
    canCreateTeamsMeetings?: boolean;
    mailboxType?: MailboxType;
    displayName?: string | null;
}

export interface UpdateMailboxAccessInput {
    mailAccess?: MailboxMailAccess;
    calendarAccess?: MailboxCalendarAccess;
    canCreateTeamsMeetings?: boolean;
    mailboxType?: MailboxType;
    displayName?: string | null;
}

const fetchJson = async <T>(input: string, init?: RequestInit): Promise<T> => {
    const res = await fetch(input, init);
    if (!res.ok) {
        const text = await res.text().catch(() => "");
        let message = `${res.status} ${res.statusText}`;
        try {
            const parsed = JSON.parse(text);
            if (parsed?.message) {
                message = Array.isArray(parsed.message)
                    ? parsed.message.join(", ")
                    : String(parsed.message);
            }
        } catch {
            if (text) message = text;
        }
        throw new Error(message);
    }
    return res.json();
};

/** B9neu — Ergebnis der Microsoft-Erreichbarkeits-Probe (App-Ebene). */
export interface MailboxProbeResult {
    reachable: boolean;
    reason?: string;
}

export const mailboxAccessAdminService = {
    async list(): Promise<MailboxAccessAdmin[]> {
        return fetchJson<MailboxAccessAdmin[]>(apiUrl("admin/mailbox-access"));
    },

    /** B9neu — kann die App das Postfach bei Microsoft erreichen? */
    async probe(mailbox: string): Promise<MailboxProbeResult> {
        return fetchJson<MailboxProbeResult>(
            apiUrl("admin/mailbox-access/probe", { mailbox }),
        );
    },

    async create(input: CreateMailboxAccessInput): Promise<MailboxAccessAdmin> {
        return fetchJson<MailboxAccessAdmin>(apiUrl("admin/mailbox-access"), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(input),
        });
    },

    async update(
        id: string,
        patch: UpdateMailboxAccessInput,
    ): Promise<MailboxAccessAdmin> {
        return fetchJson<MailboxAccessAdmin>(apiUrl(`admin/mailbox-access/${id}`), {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(patch),
        });
    },

    async remove(id: string): Promise<void> {
        await fetchJson(apiUrl(`admin/mailbox-access/${id}`), {
            method: "DELETE",
        });
    },
};
